import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const BACKEND_API_URL = (import.meta.env?.VITE_API_URL || 'http://localhost:4000').replace(/\/$/, '');

const resolveAssetUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url) || url.startsWith('blob:') || url.startsWith('data:')) return url;
  return `${BACKEND_API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const formatBytes = (bytes) => {
  const numeric = Number(bytes) || 0;
  if (!numeric) return 'Unknown size';
  const mb = numeric / (1024 * 1024);
  return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
};

const formatDuration = (seconds) => {
  const numeric = Number(seconds) || 0;
  if (!numeric) return '--:--';
  const mins = Math.floor(numeric / 60);
  const secs = Math.floor(numeric % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

const ShareSessionPage = () => {
  const { token } = useParams();
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentName, setCommentName] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentError, setCommentError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadShare = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${BACKEND_API_URL}/share/${token}`);
        const text = await response.text();
        let payload = null;
        try {
          payload = text ? JSON.parse(text) : null;
        } catch {
          throw new Error('Share service returned an invalid response.');
        }
        if (!response.ok) throw new Error(payload?.error || 'Share link could not be opened.');
        if (!cancelled) setShareData(payload?.data || null);
      } catch (loadError) {
        if (!cancelled) setError(loadError?.message || 'Share link could not be opened.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadShare();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const files = shareData?.audio_files || [];
  const beatFile = files.find((file) => file?.file_role === 'beat');
  const takeFiles = files.filter((file) => file?.file_role !== 'beat');
  const comments = shareData?.comments || [];
  const canComment = shareData?.share?.permission === 'comment';
  const sessionTitle = shareData?.session?.title || shareData?.session?.session_data?.sessionTitle || 'Shared Session';
  const expiresAt = shareData?.share?.expires_at ? new Date(shareData.share.expires_at) : null;
  const totalSize = useMemo(
    () => files.reduce((sum, file) => sum + (Number(file?.file_size) || 0), 0),
    [files]
  );
  const handleSubmitComment = async (event) => {
    event.preventDefault();
    setCommentSaving(true);
    setCommentError(null);
    try {
      const response = await fetch(`${BACKEND_API_URL}/share/${token}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          author_name: commentName || 'Guest',
          body: commentBody,
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || 'Comment could not be posted.');
      setShareData((current) => ({
        ...current,
        comments: [...(current?.comments || []), payload?.data],
      }));
      setCommentBody('');
    } catch (submitError) {
      setCommentError(submitError?.message || 'Comment could not be posted.');
    } finally {
      setCommentSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="pt-[60px]">
        <div className="container-studio py-6 lg:py-10">
          {loading && (
            <div className="flex min-h-[50vh] items-center justify-center">
              <div className="flex items-center gap-3 text-muted-foreground">
                <Icon name="Loader" size={22} className="animate-spin" />
                <span className="text-sm">Opening shared session...</span>
              </div>
            </div>
          )}

          {!loading && error && (
            <div className="mx-auto max-w-xl rounded-lg border border-red-500/30 bg-red-950/20 p-5">
              <div className="mb-3 flex items-center gap-2 text-red-200">
                <Icon name="AlertTriangle" size={18} />
                <h1 className="text-lg font-semibold">Share link unavailable</h1>
              </div>
              <p className="text-sm text-red-100/80">{error}</p>
              <Button asChild variant="outline" className="mt-4">
                <Link to="/recording-studio">Open Recording Studio</Link>
              </Button>
            </div>
          )}

          {!loading && !error && shareData && (
            <div className="space-y-5">
              <section className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 p-4">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="mb-2 flex items-center gap-2 text-cyan-200">
                      <Icon name="Share2" size={18} />
                      <span className="text-xs font-mono uppercase tracking-[0.2em]">Shared Recording Session</span>
                    </div>
                    <h1 className="text-2xl font-heading font-semibold text-white">{sessionTitle}</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {files.length} audio asset{files.length === 1 ? '' : 's'} · {formatBytes(totalSize)}
                      {expiresAt ? ` · Expires ${expiresAt.toLocaleDateString()}` : ''}
                    </p>
                  </div>
                  <Button asChild variant="default" iconName="Mic2" iconPosition="left">
                    <Link to="/recording-studio">Open Studio</Link>
                  </Button>
                </div>
              </section>

              {beatFile && (
                <section className="rounded-lg border border-gray-800 bg-card p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-gray-300">Beat</h2>
                      <p className="text-xs text-muted-foreground">{beatFile.filename}</p>
                    </div>
                    <span className="rounded border border-gray-700 px-2 py-1 text-[10px] font-mono text-gray-400">
                      {formatDuration(beatFile.duration)}
                    </span>
                  </div>
                  <audio controls src={resolveAssetUrl(beatFile.public_url || beatFile.file_path)} className="w-full" />
                </section>
              )}

              <section className="rounded-lg border border-gray-800 bg-card p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-gray-300">Vocal Takes</h2>
                  <span className="text-xs text-muted-foreground">{takeFiles.length} take{takeFiles.length === 1 ? '' : 's'}</span>
                </div>
                <div className="space-y-3">
                  {takeFiles.map((file) => (
                    <div key={file.id} className="rounded border border-gray-800 bg-gray-950/60 p-3">
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium text-white">
                            {[file.section_id, file.channel_id].filter(Boolean).join(' ') || file.filename}
                          </p>
                          <p className="text-xs text-muted-foreground">{file.filename} · {formatBytes(file.file_size)}</p>
                        </div>
                        <span className="rounded border border-gray-700 px-2 py-1 text-[10px] font-mono text-gray-400">
                          {formatDuration(file.duration)}
                        </span>
                      </div>
                      <audio controls src={resolveAssetUrl(file.public_url || file.file_path)} className="w-full" />
                    </div>
                  ))}

                  {!takeFiles.length && (
                    <p className="rounded border border-gray-800 bg-gray-950/60 p-3 text-sm text-muted-foreground">
                      This shared session does not include vocal takes yet.
                    </p>
                  )}
                </div>
              </section>

              <section className="rounded-lg border border-gray-800 bg-card p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-gray-300">Comments</h2>
                  <span className="text-xs text-muted-foreground">{comments.length} note{comments.length === 1 ? '' : 's'}</span>
                </div>

                <div className="space-y-2">
                  {comments.map((comment) => (
                    <div key={comment.id || `${comment.author_name}-${comment.created_at}`} className="rounded border border-gray-800 bg-gray-950/60 p-3">
                      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-medium text-white">{comment.author_name || 'Guest'}</p>
                        <span className="text-[10px] font-mono text-gray-500">
                          {comment.timestamp_seconds != null ? `${formatDuration(comment.timestamp_seconds)} · ` : ''}
                          {comment.created_at ? new Date(comment.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Just now'}
                        </span>
                      </div>
                      <p className="text-sm leading-relaxed text-gray-300">{comment.body}</p>
                    </div>
                  ))}

                  {!comments.length && (
                    <p className="rounded border border-gray-800 bg-gray-950/60 p-3 text-sm text-muted-foreground">
                      No comments yet.
                    </p>
                  )}
                </div>

                {canComment ? (
                  <form onSubmit={handleSubmitComment} className="mt-4 space-y-3 border-t border-gray-800 pt-4">
                    <div className="grid gap-3 md:grid-cols-[220px_1fr]">
                      <input
                        type="text"
                        value={commentName}
                        onChange={(event) => setCommentName(event.target.value)}
                        placeholder="Your name"
                        maxLength={120}
                        className="h-10 rounded border border-gray-700 bg-gray-950 px-3 text-sm text-white outline-none focus:border-cyan-500"
                      />
                      <textarea
                        value={commentBody}
                        onChange={(event) => setCommentBody(event.target.value)}
                        placeholder="Leave mix feedback..."
                        maxLength={1000}
                        required
                        rows={3}
                        className="rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-white outline-none focus:border-cyan-500"
                      />
                    </div>
                    {commentError && <p className="text-xs text-red-300">{commentError}</p>}
                    <Button type="submit" disabled={commentSaving || !commentBody.trim()} iconName="MessageSquare" iconPosition="left">
                      {commentSaving ? 'Posting...' : 'Post Comment'}
                    </Button>
                  </form>
                ) : (
                  <p className="mt-4 rounded border border-gray-800 bg-gray-950/60 p-3 text-xs text-muted-foreground">
                    This link is view-only. Ask the project owner for a comment-enabled share link to leave feedback.
                  </p>
                )}
              </section>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ShareSessionPage;
